// src/components/common/DateSelectionModal.js
import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import { lv, enUS, ru } from 'date-fns/locale';
import { FaCalendarAlt, FaClock, FaCheckCircle, FaUsers } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { supabase } from '../data/supabaseClient';
import { Button } from '../Templates';
import Modal from './Modal';
import Spinner from './Spinner';
import './DateSelectionModal.css';

/**
 * Modal for selecting a specific date from a recurring post before starting a chat
 *
 * @param {Object} props
 * @param {Object} props.post - Recurring post object
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to call when modal is closed
 * @param {function} props.onSelectDate - Function to call with selected date info
 */
const DateSelectionModal = ({ post, isOpen, onClose, onSelectDate }) => {
  const { t, i18n } = useTranslation('common');
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get date-fns locale based on current language
  const getDateLocale = () => {
    switch (i18n.language) {
      case 'lv':
        return lv;
      case 'ru':
        return ru;
      case 'en':
      default:
        return enUS;
    }
  };

  useEffect(() => {
    if (isOpen && post?.id) {
      fetchAvailableDates();
    }
  }, [isOpen, post?.id]);

  const fetchAvailableDates = async () => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase
        .from('post_occurrences')
        .select('id, occurrence_date, occurrence_time, status, rides_count')
        .eq('parent_post_id', post.id)
        .eq('status', 'active')
        .gte('occurrence_date', new Date().toISOString().split('T')[0])
        .order('occurrence_date', { ascending: true })
        .limit(20); // Show next 20 dates

      if (fetchError) throw fetchError;

      setAvailableDates(data || []);

      // Auto-select first date
      if (data && data.length > 0) {
        setSelectedDate(data[0].id);
      }
    } catch (err) {
      console.error('Error fetching dates:', err);
      setError(t('recurring.error_loading_dates'));
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    if (!selectedDate) return;

    const selected = availableDates.find(d => d.id === selectedDate);
    if (selected) {
      onSelectDate({
        occurrenceId: selected.id,
        date: selected.occurrence_date,
        time: selected.occurrence_time
      });
      onClose();
    }
  };

  const handleClose = () => {
    setSelectedDate(null);
    onClose();
  };

  if (!post) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('recurring.select_date_title')}
      subtitle={`${post.address_from_city} → ${post.address_to_city}`}
      modalSize="medium"
    >
      <div className="dsm-content">
        {loading ? (
          <div className="dsm-loading">
            <Spinner size="medium" />
            <p>{t('recurring.loading_dates')}</p>
          </div>
        ) : error ? (
          <div className="dsm-error">
            <p className="dsm-error-message">{error}</p>
            <Button
              variant="primary"
              onClick={fetchAvailableDates}
            >
              {t('common.retry')}
            </Button>
          </div>
        ) : availableDates.length === 0 ? (
          <div className="dsm-empty">
            <FaCalendarAlt className="dsm-empty-icon" />
            <p>{t('recurring.no_available_dates')}</p>
          </div>
        ) : (
          <>
            <p className="dsm-instruction">
              {t('recurring.select_date_instruction')}
            </p>

            <div className="dsm-dates-list">
              {availableDates.map((date) => (
                <label
                  key={date.id}
                  className={`dsm-date-option ${selectedDate === date.id ? 'dsm-selected' : ''}`}
                >
                  <input
                    type="radio"
                    name="selected-date"
                    value={date.id}
                    checked={selectedDate === date.id}
                    onChange={() => setSelectedDate(date.id)}
                  />

                  <div className="dsm-date-content">
                    <div className="dsm-date-info">
                      <span className="dsm-day-name">
                        {format(parseISO(date.occurrence_date), 'EEEE', { locale: getDateLocale() })}
                      </span>
                      <span className="dsm-date-display">
                        {format(parseISO(date.occurrence_date), 'MMM dd, yyyy', { locale: getDateLocale() })}
                      </span>
                      <span className="dsm-time-display">
                        <FaClock /> {date.occurrence_time}
                      </span>
                    </div>

                    {date.rides_count > 0 && (
                      <div className="dsm-rides-badge">
                        <FaUsers /> {date.rides_count}
                      </div>
                    )}
                  </div>

                  {selectedDate === date.id && (
                    <FaCheckCircle className="dsm-check-icon" />
                  )}
                </label>
              ))}
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="dsm-actions">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={!selectedDate || loading}
          >
            {t('recurring.continue_to_chat')}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default DateSelectionModal;
